import {
  adoptLiveTalkSpeechOwner,
  currentLiveTalkSpeechOwnerExecutionId,
  liveTalkTurnQueuesBehindReply,
  restoreRetiredLiveTalkSpeechOwner,
  retireCurrentLiveTalkSpeechOwner,
} from "./live-talk-speech-ownership";
import { hasSpeechFor } from "./tts-stop-key";
import { useTTS } from "./use-tts";
import {
  muteSpeechForUserTurn,
  unmuteSpeechForUserTurn,
} from "./tts-user-speech-interruption";

function cancelOwnedSpeech(executionId: string | null): void {
  const tts = useTTS.getState();
  if (executionId) tts.clearReplyDedup(executionId);
  tts.cancel("voice_barge_in");
}

/** `steered`: the user's follow-up went into the turn still running, which
 *  answers it. Interrupting that turn's earlier speech must not silence the
 *  reply, and audio it is still playing stays. A turn queued behind the reply
 *  talks over nothing, so the reply keeps playing and the new run's speech
 *  follows it. */
export function adoptLiveTalkSpeechOwnerForReceipt(
  clientRequestId: string,
  executionId: string,
  steered = false,
): boolean {
  const previousExecutionId = currentLiveTalkSpeechOwnerExecutionId();
  const queuesBehindReply = liveTalkTurnQueuesBehindReply(clientRequestId);
  const decision = adoptLiveTalkSpeechOwner(clientRequestId, executionId);
  if (decision !== "adopted") return false;
  if (steered) unmuteSpeechForUserTurn({ executionId });
  if (previousExecutionId !== executionId && !queuesBehindReply)
    cancelOwnedSpeech(previousExecutionId);
  return true;
}

export function retireLiveTalkSpeechForUserTurn(): void {
  cancelOwnedSpeech(retireCurrentLiveTalkSpeechOwner());
}

/** A dispatched turn cancels the reply it talks over: one speaking or queued
 *  to speak. A reply still being worked on is spoken when it arrives, so the
 *  user hears that the session is alive while their turn waits behind it. */
export function retireLiveTalkSpeechForDispatchedTurn(): void {
  const executionId = currentLiveTalkSpeechOwnerExecutionId();
  if (executionId && !hasSpeechFor(executionId)) return;
  retireLiveTalkSpeechForUserTurn();
}

/** A follow-up kept until the agent's turn ends cancels only what is speaking
 *  now. It is not fed into the running turn, so that turn's later speech is
 *  still spoken, as it would be after a follow-up steered into it. */
export function interruptLiveTalkSpeechForBufferedTurn(): void {
  retireLiveTalkSpeechForDispatchedTurn();
  const executionId = restoreRetiredLiveTalkSpeechOwner();
  if (executionId) unmuteSpeechForUserTurn({ executionId });
}

export function interruptLiveTalkSpeechForUserTurn(): void | false {
  const executionId = retireCurrentLiveTalkSpeechOwner();
  const tts = useTTS.getState();
  if (executionId) {
    muteSpeechForUserTurn({ executionId });
    tts.clearReplyDedup(executionId);
  }
  return tts.cancel("user_speech");
}
